<script>
import { apiAtendimento } from '@modules/atendimento/api/apiAtendimento'
import { ICONS } from '@modules/atendimento/constants/Text'

export default {
  name: 'ChatMediaPreview',
  props: {
    mediaUrl: {
      type: String,
      default: null,
    },
    mediaMimeType: {
      type: String,
      default: null,
    },
    mediaFilename: {
      type: String,
      default: null,
    },
    messageTipo: {
      type: String,
      default: null,
    },
    // API injetada — default é o atendimento; solicitacoes passa apiComunicacao.
    // Ambas expõem fetchMediaBlob({ url }) com o mesmo contrato.
    api: {
      type: Object,
      default: () => apiAtendimento,
    },
  },
  data() {
    return {
      icons: ICONS,
      lightboxOpen: false,
      loadError: false,
      blobUrl: null,
      loading: true,
      audioPlaying: false,
      audioCurrentTime: 0,
      audioDuration: 0,
      audioPlaybackRate: 1,
      audioRates: [1, 1.5, 2],
    }
  },
  computed: {
    isSticker() {
      return this.messageTipo === 'sticker'
    },
    isImage() {
      if (this.isSticker) return false
      if (this.mediaMimeType && this.mediaMimeType.startsWith('image/'))
        return true
      if (this.messageTipo === 'imagem') return true
      return false
    },
    isAudio() {
      if (this.mediaMimeType && this.mediaMimeType.startsWith('audio/'))
        return true
      if (this.messageTipo === 'audio') return true
      return false
    },
    isVideo() {
      if (this.mediaMimeType && this.mediaMimeType.startsWith('video/'))
        return true
      if (this.messageTipo === 'video') return true
      return false
    },
    isDocument() {
      if (this.messageTipo === 'documento') return true
      return !this.isImage && !this.isAudio && !this.isVideo
    },
    errorIcon() {
      if (this.isImage || this.isSticker) return ICONS.imageOff
      if (this.isAudio) return ICONS.musicNoteOff
      if (this.isVideo) return ICONS.videoOff
      return ICONS.fileRemove
    },
    errorMessage() {
      if (this.isImage) return 'Imagem indisponível'
      if (this.isSticker) return 'Figurinha indisponível'
      if (this.isAudio) return 'Áudio indisponível'
      if (this.isVideo) return 'Vídeo indisponível'
      if (this.isDocument) return 'Documento indisponível'
      return 'Mídia indisponível'
    },
    documentIcon() {
      if (!this.mediaMimeType) return ICONS.fileDocument
      if (this.mediaMimeType.includes('pdf')) return ICONS.filePdf
      if (
        this.mediaMimeType.includes('word') ||
        this.mediaMimeType.includes('doc')
      )
        return ICONS.fileWord
      if (
        this.mediaMimeType.includes('excel') ||
        this.mediaMimeType.includes('sheet')
      )
        return ICONS.fileExcel
      return ICONS.fileDocument
    },
    audioCurrentTimeLabel() {
      return this.formatAudioTime(this.audioCurrentTime)
    },
    audioDurationLabel() {
      return this.formatAudioTime(this.audioDuration)
    },
    audioPlaybackRateLabel() {
      const r = this.audioPlaybackRate
      return (Number.isInteger(r) ? r : r.toFixed(1)) + 'x'
    },
  },
  watch: {
    mediaUrl: {
      immediate: true,
      handler(url) {
        this.fetchMedia(url)
      },
    },
  },
  beforeDestroy() {
    this.closeLightbox()
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl)
    }
  },
  methods: {
    openLightbox() {
      this.lightboxOpen = true
    },
    closeLightbox() {
      this.lightboxOpen = false
    },
    onLightboxKey(event) {
      if (event.key === 'Escape') {
        this.closeLightbox()
      }
    },
    onMediaError() {
      this.loadError = true
    },
    formatAudioTime(seconds) {
      const total = Math.max(0, Math.floor(seconds || 0))
      const m = Math.floor(total / 60)
      const s = total % 60
      return m + ':' + (s < 10 ? '0' + s : s)
    },
    onAudioLoadedMetadata(event) {
      const d = event.target.duration
      this.audioDuration = Number.isFinite(d) ? d : 0
    },
    onAudioTimeUpdate(event) {
      this.audioCurrentTime = event.target.currentTime || 0
    },
    onAudioEnded() {
      this.audioPlaying = false
      this.audioCurrentTime = 0
      const el = this.$refs.audio
      if (el) el.currentTime = 0
    },
    toggleAudioPlay() {
      const el = this.$refs.audio
      if (!el) return
      if (el.paused) {
        el.play()
        this.audioPlaying = true
      } else {
        el.pause()
        this.audioPlaying = false
      }
    },
    onAudioSeek(event) {
      const el = this.$refs.audio
      if (!el) return
      const value = Number(event.target.value)
      el.currentTime = value
      this.audioCurrentTime = value
    },
    cycleAudioRate() {
      const el = this.$refs.audio
      const rates = this.audioRates
      const idx = rates.indexOf(this.audioPlaybackRate)
      const next = rates[(idx + 1) % rates.length]
      this.audioPlaybackRate = next
      if (el) el.playbackRate = next
    },
    fetchMedia(url) {
      this.audioPlaying = false
      this.audioCurrentTime = 0
      this.audioDuration = 0
      this.audioPlaybackRate = 1
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl)
        this.blobUrl = null
      }
      this.loadError = false
      this.loading = true

      if (!url) {
        this.loading = false
        this.loadError = true
        return
      }

      this.api
        .fetchMediaBlob({ url })
        .then((response) => {
          if (
            response &&
            response.status === 200 &&
            response.value &&
            response.value.blob
          ) {
            this.blobUrl = URL.createObjectURL(response.value.blob)
            this.loading = false
            return
          }
          this.loadError = true
          this.loading = false
        })
        .catch(() => {
          this.loadError = true
          this.loading = false
        })
    },
  },
}
</script>

<template>
  <div data-test="media-preview" class="chat-media-preview">
    <!-- Loading -->
    <div
      v-if="loading"
      class="media-loading d-flex align-center justify-center pa-3 rounded"
    >
      <v-progress-circular indeterminate size="24" width="2" color="grey" />
    </div>

    <!-- Load error fallback -->
    <div
      v-else-if="loadError"
      class="media-error d-flex align-center pa-2 rounded"
    >
      <icon-svg :name="errorIcon" size="20" color="grey" class="mr-2" />
      <span class="az-text-secondary az-size-12">{{ errorMessage }}</span>
    </div>

    <!-- Sticker (larger, no background) -->
    <template v-else-if="isSticker && blobUrl">
      <img
        :src="blobUrl"
        :alt="mediaFilename || 'Sticker'"
        class="media-sticker"
        @error="onMediaError"
      />
    </template>

    <!-- Image with lightbox -->
    <template v-else-if="isImage && blobUrl">
      <img
        :src="blobUrl"
        :alt="mediaFilename || 'Mídia'"
        class="media-image rounded"
        @click="openLightbox"
        @error="onMediaError"
      />
      <!-- Lightbox overlay -->
      <div
        v-if="lightboxOpen"
        data-test="lightbox"
        class="lightbox-overlay"
        @click="closeLightbox"
        @keydown="onLightboxKey"
      >
        <button
          type="button"
          class="lightbox-close"
          @click.stop="closeLightbox"
        >
          <icon-svg :name="icons.close" size="28" color="#FFFFFF" />
        </button>
        <img
          :src="blobUrl"
          :alt="mediaFilename || 'Mídia'"
          class="lightbox-image"
          @click.stop
        />
      </div>
    </template>

    <!-- Audio player (custom AAWZ) -->
    <div
      v-else-if="isAudio && blobUrl"
      data-test="media-audio"
      class="media-audio-wrapper d-flex align-center pa-2 rounded"
    >
      <!-- Audio element escondido: controles são feitos manualmente pra dar
           seek bar grande, velocidade 1x/1.5x/2x e cores da plataforma. -->
      <audio
        ref="audio"
        :src="blobUrl"
        class="media-audio-native"
        preload="metadata"
        @loadedmetadata="onAudioLoadedMetadata"
        @timeupdate="onAudioTimeUpdate"
        @ended="onAudioEnded"
        @error="onMediaError"
      />

      <v-btn
        icon
        small
        class="audio-play-btn mr-2 flex-shrink-0"
        :aria-label="audioPlaying ? 'Pausar' : 'Reproduzir'"
        @click="toggleAudioPlay"
      >
        <icon-svg
          :name="audioPlaying ? icons.pause : icons.play"
          size="18"
          color="var(--primary)"
        />
      </v-btn>

      <input
        type="range"
        class="audio-seek flex-grow-1"
        min="0"
        :max="audioDuration || 0"
        :step="0.1"
        :value="audioCurrentTime"
        :disabled="!audioDuration"
        :aria-label="'Progresso do áudio'"
        @input="onAudioSeek"
      />

      <span class="audio-time az-text-secondary az-size-12 ml-2 flex-shrink-0">
        {{ audioCurrentTimeLabel }} / {{ audioDurationLabel }}
      </span>

      <button
        type="button"
        class="audio-rate-btn az-text-small font-medium ml-2 flex-shrink-0"
        :aria-label="'Velocidade: ' + audioPlaybackRateLabel"
        @click="cycleAudioRate"
      >
        {{ audioPlaybackRateLabel }}
      </button>
    </div>

    <!-- Video player -->
    <video
      v-else-if="isVideo && blobUrl"
      :src="blobUrl"
      controls
      class="media-video rounded"
      preload="metadata"
      @error="onMediaError"
    >
      <track kind="captions" />
    </video>

    <!-- Document download link -->
    <a
      v-else-if="blobUrl"
      :href="blobUrl"
      :download="mediaFilename || 'documento'"
      class="media-document d-flex align-center pa-2 rounded"
    >
      <icon-svg
        :name="documentIcon"
        size="24"
        color="grey darken-1"
        class="mr-2"
      />
      <div class="overflow-hidden">
        <div class="az-text-primary az-size-12 font-medium text-truncate">
          {{ mediaFilename || 'Documento' }}
        </div>
        <div class="az-text-secondary az-size-12">Clique para baixar</div>
      </div>
      <div class="flex-grow-1" />
      <icon-svg :name="icons.download" size="18" color="grey" />
    </a>
  </div>
</template>

<style lang="sass" scoped>
.media-sticker
  width: 120px
  height: 120px
  object-fit: contain

.media-image
  width: 100%
  max-width: 250px
  max-height: 200px
  object-fit: cover
  cursor: pointer
  transition: opacity 0.15s ease

.media-image:hover
  opacity: 0.9

.media-audio-wrapper
  background-color: rgba(0, 0, 0, 0.04)
  // Sem min-width: se o wrapper pedir largura mínima, a bolha
  // (max-width: 65%) é forçada a crescer além do limite e "vaza" do
  // container. O seek bar (flex-grow-1 + min-width: 0) encolhe até 0,
  // e os demais elementos (play/timer/speed) são pequenos o suficiente
  // pra caberem em painéis estreitos (popup CRM ~234px).
  width: 100%
  max-width: 100%
  min-width: 0
  box-sizing: border-box

// Elemento <audio> é só a engine de reprodução — UI é custom.
.media-audio-native
  display: none

.audio-play-btn
  background-color: var(--white) !important
  ::v-deep .v-btn__content
    opacity: 1

// Seek bar custom em primary. Uso input[type=range] + reset cross-browser
// pra ter uma barra fina (3px) com thumb pequeno, bem diferente do player
// nativo que o WhatsApp espreme em ~30px.
.audio-seek
  -webkit-appearance: none
  appearance: none
  height: 3px
  background: var(--gray-3)
  border-radius: 2px
  outline: none
  cursor: pointer
  padding: 0
  margin: 0
  // Browsers dão min-width: ~170px default pra input[type=range]. Precisa
  // forçar 0 pra ele encolher dentro do flex parent em telas estreitas.
  min-width: 0
  width: 100%

.audio-seek:disabled
  cursor: default
  opacity: 0.5

.audio-seek::-webkit-slider-thumb
  -webkit-appearance: none
  appearance: none
  width: 12px
  height: 12px
  border-radius: 50%
  background: var(--primary)
  border: none
  cursor: pointer
  transition: transform 0.15s ease

.audio-seek::-webkit-slider-thumb:hover
  transform: scale(1.2)

.audio-seek::-moz-range-thumb
  width: 12px
  height: 12px
  border-radius: 50%
  background: var(--primary)
  border: none
  cursor: pointer

.audio-seek::-moz-range-track
  height: 3px
  background: var(--gray-3)
  border-radius: 2px

.audio-time
  font-variant-numeric: tabular-nums
  white-space: nowrap

.audio-rate-btn
  background: var(--white)
  border: 1px solid var(--gray-3)
  border-radius: 10px
  padding: 2px 8px
  cursor: pointer
  color: var(--gray-7)
  min-width: 36px

.audio-rate-btn:hover
  background: var(--gray-f4)

.media-video
  width: 100%
  max-width: 250px
  max-height: 200px

.media-document
  background-color: rgba(0, 0, 0, 0.05)
  text-decoration: none
  color: inherit
  min-width: 180px

.media-document:hover
  background-color: rgba(0, 0, 0, 0.08)

.media-loading
  background-color: rgba(0, 0, 0, 0.04)
  min-width: 150px
  min-height: 60px

.media-error
  background-color: rgba(0, 0, 0, 0.04)
  min-width: 150px

// Lightbox
.lightbox-overlay
  position: fixed
  top: 0
  left: 0
  right: 0
  bottom: 0
  background-color: rgba(0, 0, 0, 0.85)
  z-index: 9999
  display: flex
  align-items: center
  justify-content: center
  cursor: pointer

.lightbox-close
  position: absolute
  top: 16px
  right: 16px
  background: rgba(0, 0, 0, 0.5)
  border: none
  cursor: pointer
  padding: 8px
  border-radius: 50%
  display: inline-flex
  align-items: center
  justify-content: center
  z-index: 10000

.lightbox-close:hover
  background: rgba(0, 0, 0, 0.7)

.lightbox-image
  max-width: 90vw
  max-height: 90vh
  object-fit: contain
  cursor: default
</style>
